import {useState, useEffect, useRef, useCallback} from '@wordpress/element';
import {sprintf} from '@wordpress/i18n';
import {DEFAULT_BILLING_ADDRESS, DEFAULT_SHIPPING_ADDRESS, i18n} from "../utils";

/**
 * Shared hook for processing payment data for express payment methods
 * @param {Object} params
 * @param {string} params.title - Payment method title
 * @param {Function} params.onSubmit - Submit callback
 * @param {Object} params.responseTypes - Response type constants
 * @param {Function} params.onPaymentSetup - Payment setup event registration
 * @param {string} params.paymentMethodId - Payment method ID (e.g., 'ppcp_googlepay')
 * @param {string} params.activePaymentMethod - Currently active payment method
 */
export const useProcessPayment = (
    {
        name,
        title,
        onSubmit,
        responseTypes,
        onPaymentSetup,
        billingAddress,
        shippingAddress,
        paymentMethodId,
        activePaymentMethod,
        needsSetupToken = false
    }) => {
    const [paymentData, updatePaymentData] = useState({});
    const currentPaymentData = useRef({});
    const currentData = useRef({billingAddress, shippingAddress});

    const setPaymentData = useCallback((value, submit = true) => {
        if (value === null || value === '') {
            updatePaymentData({});
        } else {
            updatePaymentData(prev => ({
                ...prev,
                ...value,
                submit
            }));
        }
    }, []);

    const clearPaymentData = useCallback(() => {
        updatePaymentData({});
    }, []);

    useEffect(() => {
        currentPaymentData.current = paymentData;
    }, [paymentData]);

    useEffect(() => {
        currentData.current = {
            billingAddress,
            shippingAddress,
        }
    }, [billingAddress, shippingAddress]);

    // Auto-submit when payment data is set
    useEffect(() => {
        if (paymentData && paymentData.submit) {
            onSubmit();
        }
    }, [paymentData, onSubmit]);

    // Handle payment setup for order submission
    useEffect(() => {
        if (activePaymentMethod === name) {
            const unsubscribe = onPaymentSetup(() => {
                const {
                    email,
                    orderId = '',
                    billingAddress = null,
                    shippingAddress = null
                } = currentPaymentData.current;

                if (!orderId && !needsSetupToken) {
                    return {
                        type: responseTypes.ERROR,
                        message: sprintf(i18n.order_button_click, title)
                    }
                }

                const orderIdFieldName = `${paymentMethodId}_paypal_order_id`;

                const response = {
                    meta: {
                        paymentMethodData: {
                            [orderIdFieldName]: orderId
                        },
                        ...(billingAddress && {
                            billingAddress: {
                                // It's very important that the default billing address is used because if a key is missing
                                // WooCommerce will not populate the checkout fields.
                                ...DEFAULT_BILLING_ADDRESS,
                                ...currentData.current.billingAddress,
                                ...billingAddress,
                                ...(email && {email})
                            }
                        }),
                        ...(shippingAddress && {
                            shippingAddress: {
                                ...DEFAULT_SHIPPING_ADDRESS,
                                ...currentData.current.shippingAddress,
                                ...shippingAddress
                            }
                        })
                    }
                };

                return {type: responseTypes.SUCCESS, ...response};
            });

            return () => unsubscribe();
        }
    }, [
        name,
        title,
        responseTypes,
        onPaymentSetup,
        paymentMethodId,
        activePaymentMethod,
        needsSetupToken
    ]);

    return {
        paymentData,
        setPaymentData,
        clearPaymentData
    };
}

export default useProcessPayment;